import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  Divider,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import { 
  Person, 
  Email,
  Phone,
  Lock,
  AdminPanelSettings,
  PersonAdd
} from '@mui/icons-material';
import { 
  EasyCheckButton, 
  EasyCheckTextField,
  EasyCheckSelect 
} from '../../common/Controls';
import { FormFieldGroup, FormFieldItem } from '../../common/Forms';
import { useSnackbar } from '../../../hooks/useSnackbar';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import { useUserService } from '../../../services/users/userService';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { companyPreferencesService } from '../../../services/settings/companyPreferencesService';
import { MODULE_KEYS, MODULE_IDS, isFullAccessSelection } from '../../../utils/userPermissions';

const buildFullSelection = () => {
  const selection = {};
  MODULE_IDS.forEach((id) => { selection[id] = true; });
  return selection;
};

const AddUserForm = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [permissionsEnabled, setPermissionsEnabled] = useState(false);
  const [modulePermissions, setModulePermissions] = useState(buildFullSelection);
  const { user: currentUser } = useEasyCheck();
  const { snackbar, hideSnackbar, showSuccess, showError } = useSnackbar();
  const userService = useUserService();

  useEffect(() => {
    if (open) {
      companyPreferencesService.getPreferences().then((result) => {
        if (result.success) setPermissionsEnabled(result.data.enableUserPermissions === true);
      });
      setModulePermissions(buildFullSelection());
    }
  }, [open]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      password: '',
      phoneNumber: '',
      role: 'User'
    }
  });

  const selectedRole = watch('role');

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const userData = {
        ...data,
        companyId: currentUser.companyId,
        companyName: currentUser.companyName
      };

      // Include module permissions only when restricted (all checked = full access = omit)
      if (permissionsEnabled && data.role === 'User' && !isFullAccessSelection(modulePermissions)) {
        userData.permissions = modulePermissions;
      }

      const result = await userService.addUserToCompany(userData);
      
      if (result.success) {
        showSuccess('User added successfully! Verification email sent.');
        reset();
        onSuccess?.();
        onClose();
      } else {
        showError(result.message || 'Failed to add user');
      }
    } catch (error) {
      showError('Failed to add user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const emailPattern = {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    message: 'Please enter a valid email address'
  };

  const phonePattern = {
    value: /^[\+]?[1-9][\d]{0,15}$/,
    message: 'Please enter a valid phone number'
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonAdd color="primary" />
            <Typography variant="h6">Add New User</Typography>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Alert severity="info">
                The new user will be added to <strong>{currentUser?.companyName}</strong> and will receive an email to verify their account.
              </Alert>

              <Divider />

              {/* Personal Information Section */}
              <FormFieldGroup 
                label="Personal Information" 
                chipColor="primary"
                showDivider={false}
              >
                <FormFieldItem xs={12} sm={6}>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: 'First name is required' }}
                    render={({ field }) => (
                      <EasyCheckTextField
                        {...field}
                        variant="form"
                        label="First Name"
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </FormFieldItem>

                <FormFieldItem xs={12} sm={6}>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{ required: 'Last name is required' }}
                    render={({ field }) => (
                      <EasyCheckTextField
                        {...field}
                        variant="form"
                        label="Last Name"
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </FormFieldItem>

                <FormFieldItem xs={12}>
                  <Controller
                    name="username"
                    control={control}
                    rules={{ 
                      required: 'Email is required',
                      pattern: emailPattern
                    }}
                    render={({ field }) => (
                      <EasyCheckTextField
                        {...field}
                        variant="form"
                        label="Email Address"
                        type="email"
                        error={!!errors.username}
                        helperText={errors.username?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </FormFieldItem>

                <FormFieldItem xs={12}>
                  <Controller
                    name="phoneNumber"
                    control={control}
                    rules={{ pattern: phonePattern }}
                    render={({ field }) => (
                      <EasyCheckTextField
                        {...field}
                        variant="form"
                        label="Phone Number (Optional)"
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </FormFieldItem>
              </FormFieldGroup>

              {/* Account Settings Section */}
              <FormFieldGroup 
                label="Account Settings" 
                chipColor="secondary"
                showDivider={true}
                dividerText="Security & Access"
              >
                <FormFieldItem xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ 
                      required: 'Password is required',
                      minLength: { value: 6, message: 'Password must be at least 6 characters' }
                    }}
                    render={({ field }) => (
                      <EasyCheckTextField
                        {...field}
                        variant="form"
                        label="Temporary Password"
                        type="password"
                        error={!!errors.password}
                        helperText={errors.password?.message || 'User will be prompted to change this on first login'}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </FormFieldItem>

                <FormFieldItem xs={12}>
                  <Controller
                    name="role"
                    control={control}
                    rules={{ required: 'Role is required' }}
                    render={({ field }) => (
                      <EasyCheckSelect
                        {...field}
                        variant="form"
                        label="User Level"
                        error={!!errors.role}
                        helperText={errors.role?.message}
                        startAdornment={
                          <InputAdornment position="start">
                            <AdminPanelSettings color="primary" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="User">User</MenuItem>
                        <MenuItem value="Admin">Admin</MenuItem>
                      </EasyCheckSelect>
                    )}
                  />
                </FormFieldItem>

                {permissionsEnabled && selectedRole === 'User' && (
                  <FormFieldItem xs={12}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Module Access
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        All modules checked = full access (includes future modules)
                      </Typography>
                      <FormGroup row>
                        {MODULE_KEYS.map((module) => (
                          <FormControlLabel
                            key={module.id}
                            sx={{ width: { xs: '100%', sm: '50%' }, mr: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={modulePermissions[module.id] === true}
                                onChange={(e) =>
                                  setModulePermissions((prev) => ({ ...prev, [module.id]: e.target.checked }))
                                }
                              />
                            }
                            label={
                              <Tooltip title={module.description} placement="right">
                                <Typography variant="body2">{module.label}</Typography>
                              </Tooltip>
                            }
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  </FormFieldItem>
                )}
              </FormFieldGroup>

              <Alert severity="warning">
                <strong>User Level Permissions:</strong>
                <br />
                • <strong>User:</strong> Can manage cheques, beneficiaries, and view reports
                <br />
                • <strong>Admin:</strong> Full access including user management and company settings
              </Alert>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 2 }}>
            <EasyCheckButton 
              onClick={handleClose}
              variant="outlined"
              disabled={loading}
            >
              Cancel
            </EasyCheckButton>
            <EasyCheckButton 
              type="submit"
              loading={loading}
              startIcon={<PersonAdd />}
            >
              Add User
            </EasyCheckButton>
          </DialogActions>
        </form>
      </Dialog>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transition="slide"
        showIcon={true}
      />
    </>
  );
};

export default AddUserForm; 